import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { Mutations, Queries } from '@shared/api';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { toComponentOneOf } from '@shared/util/workflow';

type requiredWorkflowTemplateProperties =
  | 'components'
  | 'archivedComponents'
  | 'name'
  | 'requiresName'
  | 'behavior'
  | 'channelDefaultConfiguration';

export type WorkflowTemplateFormData = Pick<
  API.WorkflowTemplateFragment,
  requiredWorkflowTemplateProperties
>;

export function useCreateWorkflowTemplate(isSubform: boolean) {
  const [createWorkflow, { loading, error }] = useMutation<
    API.CreateWorkflowTemplateMutation,
    API.CreateWorkflowTemplateMutationVariables
  >(Mutations.workflow.createWorkflow);

  return [
    useCallback(
      async (
        workflowTemplate: WorkflowTemplateFormData,
      ): Promise<API.WorkflowTemplateFragment | undefined> => {
        const result = await createWorkflow({
          variables: {
            input: {
              name: workflowTemplate.name,
              requiresName: workflowTemplate.requiresName,
              isSubform,
              components: workflowTemplate.components.map(toComponentOneOf).filter(notNil),
            },
          },
          refetchQueries: [
            {
              query: Queries.workflow.listWorkflowTemplates,
              variables: { input: { isPublic: false, isSubform: true } },
            },
          ],
          awaitRefetchQueries: true,
        });
        return result?.data?.createWorkflowTemplate as API.WorkflowTemplateFragment | undefined;
      },
      [createWorkflow, isSubform],
    ),
    { loading, error },
  ] as const;
}
